<template>
  <v-container fluid class="vip-profile-container">
    <v-card class="vip-profile-card">
      <!-- Header with #00321E background -->
      <v-card-title class="profile-card-header text-white text-h5 text-center font-weight-bold">
        VIP Customer Profile
      </v-card-title>
      <!-- Top divider (80% width) -->
      <v-divider class="custom-divider"></v-divider>

      <template v-if="vip">
        <!-- Top Section: Centered Profile Picture and VIP Info -->
        <v-card-text class="pa-5 vip-top-section">
          <div class="content-wrapper">
            <!-- Profile Picture with revolving ring behind -->
            <div class="profile-picture-wrapper">
              <img
                :src="rotatingLogoUrl"
                alt="Rotating Logo Ring"
                class="rotating-logo-ring"
                @error="useDefaultRotatingLogo"
              />
              <div class="avatar">
                <v-avatar size="140">
                  <img
                    :src="vip.profile_picture_url || defaultProfilePicture"
                    alt="VIP Profile Picture"
                    class="profile-img"
                    :style="profileImgStyle"
                    @error="useDefaultProfilePicture"
                  />
                </v-avatar>
              </div>
            </div>
            <!-- VIP Profile Information -->
            <div class="profile-info text-center">
              <div class="vip-full-name">
                <strong>{{ vip.user.first_name || 'N/A' }} {{ vip.user.last_name || 'N/A' }}</strong>
              </div>
              <div class="vip-role">VIP</div>
            </div>
          </div>
        </v-card-text>

        <!-- Divider after top section -->
        <v-divider class="custom-divider"></v-divider>

        <!-- Discounts Section (from vip.discounts) -->
        <v-card-text class="discounts-section text-center" v-if="groupedDiscountsKeys.length">
          <p class="discount-header">Discounts:</p>
          <div v-for="(discounts, bar) in groupedDiscounts" :key="bar" class="bar-section">
            <h3 :class="['bar-header', barTextClass(bar)]">{{ bar }}</h3>
            <v-list dense class="mx-auto" style="max-width: 300px;">
              <v-list-item
                v-for="discount in discounts"
                :key="discount.id"
                class="discount-item"
              >
                  <v-list-item-title class="text-center">
                    {{ discount.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="discount.description" class="text-center">
                    {{ discount.description }}
                  </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </div>
        </v-card-text>

      </template>

      <!-- Error Message -->
      <v-alert v-else-if="error" type="error" prominent>
        Unable to load VIP profile information. Please try again later.
      </v-alert>
    </v-card>

  <!-- Primary Wallet action -->
  <v-card-actions class="justify-center wallet-primary-trigger">
    <v-btn class="wallet-primary-btn" :to="{ name: 'WalletPage' }">
      <SvgIcon :path="mdiWalletOutline" size="18" class="wallet-primary-icon" />
      <span>Open Wallet</span>
    </v-btn>
  </v-card-actions>

  <!-- Terms & Conditions Bottom Sheet Trigger (now Edit Profile button) -->
  <v-card-actions class="justify-center terms-trigger">
    <v-btn text small class="terms-btn" :to="{ name: 'EditVIPProfile' }">
      Edit Profile
    </v-btn>
  </v-card-actions>

  <!-- Floating Terms & Conditions button -->
  <v-card-actions class="justify-center terms-fixed-trigger">
    <v-btn text small class="terms-btn" @click="showTerms = true">
      Terms &amp; Conditions
    </v-btn>
  </v-card-actions>

  <v-card-actions class="justify-center delete-trigger">
    <v-btn text small class="delete-btn" @click="openDeleteDialog">
      Delete Account
    </v-btn>
  </v-card-actions>

  <!-- Terms & Conditions Dialog -->
  <v-dialog v-model="showTerms" scrollable max-width="600" transition="dialog-bottom-transition">
    <v-card class="mx-auto">
      <v-card-title class="headline text-center">Terms &amp; Conditions</v-card-title>
      <v-divider />
      <v-card-text class="pa-4" style="max-height: 60vh; overflow-y: auto;">
        <div class="terms-content" v-html="termsContent"></div>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="showTerms = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="showDeleteDialog" max-width="480">
    <v-card>
      <v-card-title class="text-h6">Delete Account</v-card-title>
      <v-card-text>
        <p class="mb-4">
          This permanently deletes your account and signs you out. Historical operational records are kept without your personal account data. Audit and security logs may retain identifying information and request metadata, such as IP address, about actions taken in the app for up to 180 days before they are permanently deleted.
        </p>
        <v-text-field
          v-model="deletePassword"
          label="Current Password"
          type="password"
          autocomplete="current-password"
          :disabled="isDeleting"
          @input="deleteError = ''"
        />
        <v-alert
          v-if="deleteError"
          type="error"
          variant="tonal"
          density="comfortable"
          class="mt-2"
        >
          {{ deleteError }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="isDeleting" @click="closeDeleteDialog">Cancel</v-btn>
        <v-btn
          color="error"
          :loading="isDeleting"
          :disabled="isDeleting || !deletePassword.trim()"
          @click="confirmDeleteAccount"
        >
          Permanently Delete
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";
import { mdiWalletOutline } from "@mdi/js";
import { useUserStore } from "@/stores/user";
export default {
  name: "CustomerProfile",
  data() {
    return {
      userStore: null,
      vip: null,
      defaultProfilePicture: "/media/profile_pictures/default_profile.png",
      rotatingLogoUrl: "/static/images/logos/vip_guest_v3.svg",
      error: false,
      showTerms: false,
      termsContent: '',
      mdiWalletOutline,
      showDeleteDialog: false,
      deletePassword: "",
      deleteError: "",
      isDeleting: false,
    };
  },
  computed: {
    // Group persistent VIP discounts (from vip.discounts) by bar.
    groupedDiscounts() {
      const groups = {};
      if (this.vip && this.vip.discounts) {
        this.vip.discounts.forEach((discount) => {
          if (discount.expiration && new Date(discount.expiration) < new Date()) return;
          const bar = discount.bar || "Unknown Bar";
          if (!groups[bar]) groups[bar] = [];
          groups[bar].push(discount);
        });
      }
      return groups;
    },
    groupedDiscountsKeys() {
      return Object.keys(this.groupedDiscounts);
    },
    // VIP profile picture border style.
    profileImgStyle() {
      return { border: "3px solid #00321E" };
    },
  },
  methods: {
    async fetchVIPProfile() {
      try {
        const res = await axios.get("/api/vip-profile/", { withCredentials: true });
        this.vip = res.data;
      } catch (err) {
        console.error("Error fetching VIP profile:", err);
        this.error = true;
      }
    },
    useDefaultProfilePicture(e) {
      e.target.src = this.defaultProfilePicture;
    },
    useDefaultRotatingLogo(e) {
      e.target.src = this.rotatingLogoUrl;
    },
    barTextClass(bar) {
      // Use the same logic as in the staff profile card.
      if (bar === "Temple Bar") return "text-temple";
      else if (bar === "Stefanshus") return "text-stefanshus";
      else if (bar === "Klovnen") return "text-klovnen";
      else return "";
    },
    async fetchTerms() {
      try {
        const resp = await axios.get('/api/terms-and-conditions/', { withCredentials: true });
        this.termsContent = resp.data.content || '';
      } catch (e) {
        console.error('Error fetching terms:', e);
      }
    },
    openDeleteDialog() {
      this.deletePassword = "";
      this.deleteError = "";
      this.showDeleteDialog = true;
    },
    closeDeleteDialog() {
      if (this.isDeleting) return;
      this.showDeleteDialog = false;
      this.deletePassword = "";
      this.deleteError = "";
    },
    async confirmDeleteAccount() {
      if (this.isDeleting) return;
      const password = this.deletePassword.trim();
      if (!password) {
        this.deleteError = "Current password is required.";
        return;
      }

      this.isDeleting = true;
      this.deleteError = "";

      try {
        await this.userStore.deleteAccount(password);
      } catch (error) {
        const data = error?.response?.data || {};
        if (Array.isArray(data.password) && data.password.length) {
          this.deleteError = data.password[0];
        } else if (typeof data.detail === "string" && data.detail) {
          this.deleteError = data.detail;
        } else if (typeof data.error === "string" && data.error) {
          this.deleteError = data.error;
        } else {
          this.deleteError = "Failed to delete account. Please try again.";
        }
      } finally {
        this.isDeleting = false;
      }
    },
  },
  async created() {
    this.userStore = useUserStore();
    const p1 = this.fetchVIPProfile();
    const p2 = this.fetchTerms();
    await Promise.all([p1, p2]);
  },
};
</script>

<style scoped>
/* Background and Card */
.vip-profile-container {
  background: linear-gradient(to bottom, #fafafa, #e7effa, #d2e3f5);
  min-height: 100vh;
  padding: 30px 20px;
}
.vip-profile-card {
  max-width: 500px;
  margin: 0 auto;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}
/* Header styling */
.profile-card-header {
  background-color: #00321E;
  font-size: 18px;
  font-weight: bold;
  padding: 10px;
  color: #000;
}
/* Profile Info */
.profile-info {
  font-size: 1.1rem;
  margin-bottom: 0;
}

.vip-full-name {
  font-size: 1.3rem;
  line-height: 1.2;
  margin-bottom: 6px;
}

.vip-role {
  color: #7b7b7b;
  font-size: 1rem;
  font-weight: 500;
}

.discounts-section {
  padding-top: 14px !important;
  padding-bottom: 14px !important;
}

.vip-top-section {
  padding-bottom: 12px !important;
}

/* Persistent Discounts Section */
.discount-header {
  font-size: 1.1rem;
  margin-top: 0;
  margin-bottom: 8px;
  font-weight: bold;
}
.bar-section {
  /* Reduced bottom margin to lessen the space toward the divider */
  margin-bottom: 20px;
}
/* Adjust space for the last discount group only */
.bar-section:last-child {
  margin-bottom: 12px; /* Change this value as needed for desired spacing */
}
/* VIP Discounts List Styling */
.v-list-item::before {
  content: none;
}
.v-list {
  padding: 0;
  gap: 2px;
}
.v-list-item {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  min-height: 28px !important;
}
.v-list-item-content {
  margin-bottom: 0;
}
.v-list-item-title,
.v-list-item-subtitle {
  line-height: 1.2;
  margin: 0;
}
.discount-item {
  margin-bottom: 1px;
}
/* One-Time Discounts Section */
.dash-separator {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0.4rem 0;
}
.custom-dash {
  font-size: 1.2rem;
  display: inline-block;
  /* Adjusted translateY to center in the middle; tweak as needed */
  transform: translateY(4px);
  color: rgba(0, 0, 0, 0.8);
}
.group-separator {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 10px 0;
}
.custom-star {
  color: rgba(0, 0, 0, 0.8) !important;
  margin-top: 4px;
  transform: translateY(2px);
}
/* Bar name text color classes */
.text-temple {
  color: #541B21;
}
.text-stefanshus {
  color: #156273;
}
.text-klovnen {
  color: #E47C25;
}
/* Custom divider styling: 80% width and adjust vertical margins for bottom-divider */
.custom-divider {
  width: 80%;
  margin: 0 auto;
}
.bottom-divider {
  margin-top: 10px !important;
  margin-bottom: 10px !important;
}
/* Profile Picture and Revolving Ring */
.profile-img {
  width: 140px;
  height: 140px;
  object-fit: cover;
  border-radius: 50%;
}
.content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.profile-picture-wrapper {
  position: relative;
  width: 170px;
  height: 170px;
  margin: 5px auto 20px auto;
}
.rotating-logo-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 170px;
  height: 170px;
  object-fit: contain;
  animation: rotate 5s linear infinite;
  z-index: 0;
}
.avatar {
  position: absolute;
  top: 15px;
  left: 15px;
  z-index: 1;
}
@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Limit the width of the T&C button wrapper to match the card */
.terms-trigger {
  max-width: 500px;
  width: 100% !important;
  margin: 0 auto;
  padding: 0 !important;
  min-height: 0 !important;
  display: flex !important;
  justify-content: center !important;
}

.delete-trigger {
  max-width: 500px;
  width: 100% !important;
  margin: 0 auto;
  padding: 0 !important;
  min-height: 0 !important;
  display: flex !important;
  justify-content: center !important;
}

/* Center lists and add spacing inside Terms & Conditions HTML */
:deep(.terms-content ul) {
  padding-left: 1.5rem;
  margin: 0.5rem 0;
}
:deep(.terms-content li) {
  margin-bottom: 0.5rem;
}

/* Limit bottom-sheet width, center it, and restore white background */
:deep(.v-overlay__content .v-bottom-sheet__sheet) {
  max-width: 600px !important;
  margin: 0 auto !important;
  background-color: white !important;
  border-radius: 8px !important;
}

/* Center icon + text inside the button */
.terms-btn {
  width: auto !important;
  min-width: 0 !important;
  margin: 0 auto !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0.5rem 1rem !important;
}

.delete-btn {
  width: auto !important;
  min-width: 0 !important;
  margin: 0 auto !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0.5rem 1rem !important;
  color: #c62828 !important;
}

.wallet-primary-trigger {
  max-width: 500px;
  width: 100% !important;
  margin: 4px auto 0;
  padding: 0 !important;
  display: flex !important;
  justify-content: center !important;
}

.wallet-primary-btn {
  min-width: 220px !important;
  border-radius: 999px !important;
  padding: 0 18px !important;
  height: 42px !important;
  background: linear-gradient(135deg, #131826 0%, #20293f 100%) !important;
  color: #f6f8fc !important;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 700 !important;
  box-shadow: 0 8px 18px rgba(21, 28, 43, 0.22);
  transition: transform 150ms ease, box-shadow 150ms ease, filter 150ms ease;
}

.wallet-primary-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 22px rgba(21, 28, 43, 0.28);
  filter: brightness(1.03);
}

.wallet-primary-icon {
  margin-right: 8px;
  opacity: 0.95;
}

/* Floating Terms & Conditions button wrapper */
.terms-fixed-trigger {
  max-width: 500px;
  margin: 5px auto 0;    /* 5px gap above; adjust this value to move closer or farther */
  padding: 0;
  min-height: 0 !important;
  display: flex;
  justify-content: center;
}

/* bar names bold */
.bar-section h3,
.discount-group h3 {
  font-size: 1.1rem;
  font-weight: bold;
  line-height: 1.2;
  margin-top: 4px;
  margin-bottom: 2px;
}
/* discount texts normal weight */
.v-list-item-title,
.one-time-discounts .one-time-discount-text {
  font-size: 1.1rem;
  font-weight: normal;
  line-height: 1.2;
  margin-top: 4px;
  margin-bottom: 2px;
}

/* Add margin to push Redeem button down slightly */
.redeem-btn {
  margin-top: 0.5rem;
}
</style>
